/**
 * Account "backoffice" — riconoscimento dall'email.
 *
 * Un account il cui indirizzo inizia per `b.` (es. `b.emanuela@...`) e'
 * un account di backoffice: viene nascosto da tutte le liste operatori e
 * vede gli appuntamenti che agli altri account restano nascosti.
 *
 * QUESTA FUNZIONE E' USATA SOLO SERVER-SIDE PER IL PROVISIONING.
 * Le Firestore Rules NON guardano `request.auth.token.email`: l'app non
 * espone `updateEmail`, ma un utente autenticato puo' cambiare la propria
 * email chiamando direttamente l'API REST Identity Toolkit con il suo ID
 * token, guadagnandosi il prefisso `b.` da solo. L'enforcement passa dal
 * custom claim `bo`, che solo l'Admin SDK puo' scrivere.
 *
 * Mirror per le Cloud Functions: `functions/src/lib/backoffice.ts`
 * (guard di parita' in `functions/src/__tests__/backoffice-parity.test.ts`).
 */

/** Prefisso email che identifica un account di backoffice. */
export const BACKOFFICE_EMAIL_PREFIX = 'b.';

/** Nome del custom claim che porta il flag lato token. */
export const BACKOFFICE_CLAIM = 'bo';

/**
 * Nome del custom claim che porta la DELEGA di un account di backoffice
 * con ruolo DIETOLOGA: contiene l'uid della dietologa di cui l'account
 * consulta i pazienti.
 *
 * PERCHE' ESISTE. Per il ruolo DIETOLOGA ogni autorizzazione e' legata
 * all'uid: `pazienti.dietologaId == request.auth.uid` nelle Rules,
 * `where('dietologaId','==',uid)` nelle query di agenda. Un account di
 * backoffice separato ha un uid nuovo, quindi senza delega non e'
 * assegnatario di nulla e non vedrebbe alcun paziente.
 *
 * VALE IN LETTURA E IN SCRITTURA, e solo per gli account con claim `bo`:
 * `isAssignedDietologa` in firestore.rules usa lo scope delegato in
 * entrambe le direzioni, e le callable fanno lo stesso via
 * `functions/src/lib/scope.ts`. L'account `b.` e' un secondo accesso allo
 * stesso ruolo operativo, non un utente distinto: non ha un'agenda ne'
 * pazienti propri, quindi opera su quelli della delegante (decisione del
 * titolare, 25/08/2026).
 *
 * CONSEGUENZA: e' un privilegio operativo pieno, non una consultazione.
 * Chi controlla l'account di backoffice puo' firmare atti clinici a nome
 * della delegante; la distinzione resta solo nell'audit, che registra
 * sempre l'uid reale del chiamante.
 */
export const BACKOFFICE_DELEGA_CLAIM = 'bodeleg';

/**
 * True se l'email appartiene a un account di backoffice.
 * Case-insensitive, tollerante a spazi accidentali. Email assente = false.
 */
export function isBackofficeEmail(email: string | null | undefined): boolean {
  if (typeof email !== 'string') return false;
  return email.trim().toLowerCase().startsWith(BACKOFFICE_EMAIL_PREFIX);
}

/**
 * Uid delegato letto dal claim. Ritorna null per qualunque forma che non
 * sia una stringa non vuota: un claim malformato non deve tradursi in uno
 * scope indefinito, che nelle query diventerebbe "nessun filtro".
 */
export function parseDelegaClaim(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}
